import React from "react";

import {
  FaChevronLeft,
  FaChevronRight,
  FaExpand,
  FaPause,
  FaPlay,
  FaRegImages,
} from "react-icons/fa";

import TournamentGalleryFilters from "./components/TournamentGalleryFilters";
import TournamentGalleryLightbox from "./components/TournamentGalleryLightbox";

import useTournamentGallery from "./hooks/useTournamentGallery";

import {
  TOURNAMENT_GALLERY_CATEGORIES,
  TOURNAMENT_GALLERY_SLIDES,
  TOURNAMENT_GALLERY_STATS,
} from "./data/tournamentGalleryData";

import {
  cx,
} from "./utils/tournamentProductUtils";

function GalleryStatCard({
  icon: Icon,
  value,
  label,
}) {
  return (
    <article className="rounded-[24px] border border-slate-200 bg-white p-4 text-center shadow-sm dark:border-white/10 dark:bg-slate-900">
      <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-2xl bg-orange-50 text-orange-600 ring-1 ring-orange-100 dark:bg-orange-500/15 dark:text-orange-300 dark:ring-orange-400/20">
        <Icon className="h-4 w-4" />
      </div>

      <p className="mt-3 font-bold text-slate-950 dark:text-white">
        {value}
      </p>

      <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
        {label}
      </p>
    </article>
  );
}

function findCategoryLabel(
  categoryKey,
) {
  return (
    TOURNAMENT_GALLERY_CATEGORIES.find(
      (category) =>
        category.key ===
        categoryKey,
    )?.label ||
    "Application"
  );
}

export default function DiaporamaTournoi() {
  const gallery =
    useTournamentGallery({
      slides:
        TOURNAMENT_GALLERY_SLIDES,

      defaultCategory: "all",

      autoPlay: true,

      interval: 5500,
    });

  const current =
    gallery.currentSlide;

  if (!current) {
    return null;
  }

  return (
    <>
      <section className="relative overflow-hidden bg-slate-50 py-16 dark:bg-slate-900/40 sm:py-20 lg:py-24">
        <div className="pointer-events-none absolute -left-24 top-20 h-80 w-80 rounded-full bg-blue-300/20 blur-3xl dark:bg-blue-500/10" />

        <div className="pointer-events-none absolute -right-24 bottom-20 h-80 w-80 rounded-full bg-orange-300/20 blur-3xl dark:bg-orange-500/10" />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* En-tête */}
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-orange-700 dark:border-orange-400/20 dark:bg-orange-500/10 dark:text-orange-300">
              <FaRegImages />
              Aperçus de l’application
            </div>

            <h2 className="mt-5 text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl lg:text-5xl dark:text-white">
              Découvrez MonAppliTournoi écran par écran
            </h2>

            <p className="mt-5 max-w-3xl text-base leading-8 text-slate-600 sm:text-lg dark:text-slate-300">
              Parcourez les principales interfaces utilisées avant, pendant et après la compétition : préparation, pesées, répartitions, combats, scoreboards et résultats.
            </p>
          </div>

          {/* Indicateurs */}
          <div className="mt-8 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {TOURNAMENT_GALLERY_STATS.map(
              (stat) => (
                <GalleryStatCard
                  key={stat.value}
                  {...stat}
                />
              ),
            )}
          </div>

          {/* Filtres */}
          <div className="mt-8 rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-slate-900 sm:p-5">
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
              Filtrer les écrans
            </p>

            <TournamentGalleryFilters
              activeCategory={
                gallery.category
              }
              slides={
                TOURNAMENT_GALLERY_SLIDES
              }
              onChange={
                gallery.changeCategory
              }
            />
          </div>

          {/* Diaporama */}
          <div
            ref={
              gallery.containerRef
            }
            tabIndex={0}
            className="relative mt-8 outline-none"
            onMouseEnter={() =>
              gallery.setPaused(
                true,
              )
            }
            onMouseLeave={() =>
              gallery.setPaused(
                false,
              )
            }
            onTouchStart={
              gallery.onTouchStart
            }
            onTouchEnd={
              gallery.onTouchEnd
            }
          >
            <div className="overflow-hidden rounded-[34px] border border-slate-200 bg-white shadow-2xl shadow-slate-950/10 dark:border-white/10 dark:bg-slate-950/80">
              <div className="grid lg:grid-cols-[minmax(0,1fr)_360px]">
                {/* Image */}
                <div className="relative bg-slate-950 p-2 sm:p-3 lg:p-4">
                  <div className="relative overflow-hidden rounded-[26px] bg-slate-900">
                    <div className="absolute left-4 top-4 z-20 hidden items-center gap-2 rounded-full border border-white/15 bg-slate-950/40 px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] text-white backdrop-blur sm:flex">
                      <FaRegImages className="text-orange-300" />

                      {findCategoryLabel(
                        current.category,
                      )}
                    </div>

                    <div className="relative flex aspect-[4/3] items-center justify-center md:aspect-[16/10] xl:aspect-[16/9]">
                      {!gallery.loaded && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="h-12 w-12 animate-spin rounded-full border-4 border-white/15 border-t-orange-400" />
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={
                          gallery.openLightbox
                        }
                        className="group h-full w-full cursor-zoom-in"
                        aria-label={`Agrandir ${current.title}`}
                      >
                        <img
                          key={
                            current.src
                          }
                          src={
                            current.src
                          }
                          alt={
                            current.alt
                          }
                          loading="lazy"
                          onLoad={() =>
                            gallery.setLoaded(
                              true,
                            )
                          }
                          className={cx(
                            "h-full w-full object-contain transition duration-500",
                            gallery.loaded
                              ? "scale-100 opacity-100"
                              : "scale-[0.985] opacity-0",
                          )}
                        />
                      </button>
                    </div>

                    {gallery.total >
                      1 && (
                      <>
                        <button
                          type="button"
                          onClick={
                            gallery.previous
                          }
                          className="absolute left-2 top-1/2 z-20 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-2xl border border-white/15 bg-slate-950/40 text-white shadow-xl backdrop-blur transition hover:-translate-x-0.5 hover:bg-orange-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300 sm:left-4 sm:h-12 sm:w-12"
                          aria-label="Écran précédent"
                        >
                          <FaChevronLeft />
                        </button>

                        <button
                          type="button"
                          onClick={
                            gallery.next
                          }
                          className="absolute right-2 top-1/2 z-20 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-2xl border border-white/15 bg-slate-950/40 text-white shadow-xl backdrop-blur transition hover:translate-x-0.5 hover:bg-orange-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300 sm:right-4 sm:h-12 sm:w-12"
                          aria-label="Écran suivant"
                        >
                          <FaChevronRight />
                        </button>
                      </>
                    )}

                    <button
                      type="button"
                      onClick={
                        gallery.openLightbox
                      }
                      className="absolute bottom-4 right-4 z-20 hidden h-11 w-11 items-center justify-center rounded-2xl border border-white/15 bg-slate-950/40 text-white backdrop-blur transition hover:bg-orange-500 sm:inline-flex"
                      aria-label="Afficher en plein écran"
                      title="Afficher en plein écran"
                    >
                      <FaExpand />
                    </button>
                  </div>
                </div>

                {/* Informations */}
                <aside className="flex flex-col justify-between border-t border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/[0.03] sm:p-6 lg:border-l lg:border-t-0">
                  <div>
                    <div className="flex flex-wrap gap-2">
                      <span className="rounded-full bg-orange-50 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.12em] text-orange-700 dark:bg-orange-500/10 dark:text-orange-300">
                        Écran{" "}
                        {gallery.index +
                          1}{" "}
                        / {gallery.total}
                      </span>

                      <span className="rounded-full bg-blue-50 px-3 py-1.5 text-xs font-bold text-blue-700 dark:bg-blue-500/10 dark:text-blue-300">
                        {
                          current.audience
                        }
                      </span>
                    </div>

                    <h3 className="mt-5 text-2xl font-bold leading-tight text-slate-950 dark:text-white">
                      {current.title}
                    </h3>

                    <p className="mt-4 text-sm leading-7 text-slate-600 dark:text-slate-300">
                      {
                        current.description
                      }
                    </p>

                    <div className="mt-5 flex flex-wrap gap-2">
                      {current.tags.map(
                        (tag) => (
                          <span
                            key={tag}
                            className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 dark:border-white/10 dark:bg-slate-950/50 dark:text-slate-300"
                          >
                            {tag}
                          </span>
                        ),
                      )}
                    </div>
                  </div>

                  <div className="mt-8">
                    <div className="mb-4 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-blue-600 to-orange-500 transition-all duration-500"
                        style={{
                          width: `${gallery.progressPercent}%`,
                        }}
                      />
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <button
                        type="button"
                        onClick={() =>
                          gallery.setPaused(
                            (value) =>
                              !value,
                          )
                        }
                        className="inline-flex min-h-11 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-orange-200 hover:text-orange-600 dark:border-white/10 dark:bg-slate-950/50 dark:text-white dark:hover:border-orange-400/30 dark:hover:text-orange-300"
                      >
                        {gallery.paused ? (
                          <>
                            <FaPlay />
                            Lecture
                          </>
                        ) : (
                          <>
                            <FaPause />
                            Pause
                          </>
                        )}
                      </button>

                      <div className="flex items-center gap-2">
                        {gallery.filteredSlides.map(
                          (
                            slide,
                            slideIndex,
                          ) => (
                            <button
                              key={
                                slide.id
                              }
                              type="button"
                              onClick={() =>
                                gallery.goTo(
                                  slideIndex,
                                )
                              }
                              className={cx(
                                "h-2.5 rounded-full transition-all",
                                slideIndex ===
                                  gallery.index
                                  ? "w-8 bg-orange-500"
                                  : "w-2.5 bg-slate-300 hover:bg-slate-400 dark:bg-white/20 dark:hover:bg-white/40",
                              )}
                              aria-label={`Afficher l’écran ${slideIndex + 1}`}
                            />
                          ),
                        )}
                      </div>
                    </div>
                  </div>
                </aside>
              </div>

              {/* Miniatures */}
              <div className="border-t border-slate-200 bg-white/90 px-3 py-3 dark:border-white/10 dark:bg-slate-950/60 sm:px-4">
                <div
                  ref={
                    gallery.thumbnailsRef
                  }
                  className="flex snap-x items-center gap-2 overflow-x-auto overflow-y-hidden pb-1"
                >
                  {gallery.filteredSlides.map(
                    (
                      slide,
                      slideIndex,
                    ) => {
                      const active =
                        slideIndex ===
                        gallery.index;

                      return (
                        <button
                          key={
                            slide.id
                          }
                          type="button"
                          data-gallery-thumb={
                            slideIndex
                          }
                          onClick={() =>
                            gallery.goTo(
                              slideIndex,
                            )
                          }
                          className={cx(
                            "group relative shrink-0 snap-center overflow-hidden rounded-2xl border p-1 transition",
                            active
                              ? "border-orange-400 bg-orange-50 shadow-md dark:bg-orange-500/10"
                              : "border-slate-200 bg-slate-50 hover:border-orange-200 dark:border-white/10 dark:bg-white/[0.04] dark:hover:border-orange-400/30",
                          )}
                          aria-label={`Voir ${slide.title}`}
                          title={
                            slide.title
                          }
                        >
                          <img
                            src={
                              slide.src
                            }
                            alt={
                              slide.alt
                            }
                            loading="lazy"
                            className={cx(
                              "h-14 w-24 rounded-xl object-cover transition sm:h-16 sm:w-28",
                              active
                                ? "opacity-100"
                                : "opacity-65 group-hover:opacity-100",
                            )}
                          />

                          <span
                            className={cx(
                              "absolute left-2 top-2 flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] font-bold",
                              active
                                ? "bg-orange-500 text-white"
                                : "bg-slate-950/70 text-white",
                            )}
                          >
                            {slideIndex +
                              1}
                          </span>
                        </button>
                      );
                    },
                  )}
                </div>
              </div>
            </div>

            <p className="mt-4 text-center text-xs leading-5 text-slate-400">
              Utilisez les flèches du clavier, les boutons ou un geste horizontal sur mobile pour naviguer.
            </p>
          </div>
        </div>
      </section>

      <TournamentGalleryLightbox
        open={
          gallery.lightboxOpen
        }
        slide={current}
        index={gallery.index}
        total={gallery.total}
        onClose={
          gallery.closeLightbox
        }
        onPrevious={
          gallery.previous
        }
        onNext={gallery.next}
        onTouchStart={
          gallery.onTouchStart
        }
        onTouchEnd={
          gallery.onTouchEnd
        }
      />
    </>
  );
}